<script setup>
import {
  ref,
  computed,
  onMounted,
  onBeforeUnmount,
  watch,
  nextTick,
} from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { terminalStageKind as sharedTerminalStageKind } from 'dashboard/helper/funnelStages';
import {
  showScreenTransitionOverlay,
  paintThen,
} from 'dashboard/helper/screenTransitionOverlay';
import { useMapGetter } from 'dashboard/composables/store.js';
import { useI18n } from 'vue-i18n';
import { useCurrency } from 'dashboard/composables/useCurrency';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import ConversationFunnelMappingsAPI from 'dashboard/api/conversationFunnelMappings';
import KanbanAPI from 'dashboard/api/kanban';
import ConversationAppointmentsAPI from 'dashboard/api/conversationAppointments';
import CrmPipelineFilters from './components/kanban/CrmPipelineFilters.vue';
import {
  emptyPipelineFilters,
  pipelineParams,
} from './components/kanban/pipelineFilters';
import KanbanBoard from './components/kanban/KanbanBoard.vue';
import ConversationModal from './components/kanban/ConversationModal.vue';
import NewLeadModal from 'dashboard/components-next/NewLeadModal/NewLeadModal.vue';
import WinDealModal from './components/kanban/WinDealModal.vue';
import LoseDealModal from './components/kanban/LoseDealModal.vue';
import DealDetailsSidebar from './components/kanban/DealDetailsSidebar.vue';
import { dealActionErrorMessage } from './components/kanban/dealActionError';
import ChatTypeTabs from 'dashboard/components/widgets/ChatTypeTabs.vue';
import ConversationBasicFilter from 'dashboard/components/widgets/conversation/ConversationBasicFilter.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import ConversationFilter from 'next/filter/ConversationFilter.vue';
import TeleportWithDirection from 'dashboard/components-next/TeleportWithDirection.vue';
import { useFunnelManager } from 'dashboard/composables/kanban/useFunnelManager';
import { useMutexLock } from 'dashboard/composables/kanban/useMutexLock';
import { useKanbanStorage } from 'dashboard/composables/kanban/useKanbanStorage';
import wootConstants from 'dashboard/constants/globals';
import { ASSIGNEE_TYPE_TAB_PERMISSIONS } from 'dashboard/constants/permissions.js';
import {
  getUserPermissions,
  filterItemsByPermission,
} from 'dashboard/helper/permissionsHelper';

// Props
const props = defineProps({
  inboxId: {
    type: [String, Number],
    default: 0,
  },
  funnelId: {
    type: [String, Number],
    default: null,
  },
});
const store = useStore();
const route = useRoute();
const router = useRouter();
const { t, locale } = useI18n();
const { currency } = useCurrency();
const browserLocale = computed(() => locale.value.replace('_', '-'));

// Estado local
const selectedConversationId = ref(null);
const showConversationModal = ref(false);
const isLoadingFunnel = ref(false);
const isInitializing = ref(true);
const showAdvancedFilters = ref(false);
const appliedFilter = ref([]);
const showFunnelDropdown = ref(false);

// Estado para modal de novo lead
const newLeadModalRef = ref(null);

const openNewLeadModal = () => {
  newLeadModalRef.value?.open();
};

// Estado para modais de win/lose deal
const showWinDealModal = ref(false);
const isMarkingDealWon = ref(false);
const showLoseDealModal = ref(false);
const selectedDealConversation = ref(null);
const selectedDealMapping = ref(null);
const selectedDealValue = ref(0);
// Drop numa etapa terminal (Ganho/Perdido) guarda o destino: o card só se
// move DEPOIS que o fechamento for confirmado no modal.
const pendingTerminalMove = ref(null);

// Estado para sidebar de detalhes do deal
const showDealDetailsSidebar = ref(false);

// Estado para modal de transferência de funil
const showTransferFunnelModal = ref(false);
const transferConversation = ref(null);
const selectedTargetFunnel = ref(null);
const selectedTargetStage = ref(null);
const isTransferring = ref(false);
const removeFromSourceOnTransfer = ref(true);

// handleLeadCreated será definido mais abaixo, após fetchConversations
let handleLeadCreated;

// Estados dos filtros nativos
const activeAssigneeTab = ref(wootConstants.ASSIGNEE_TYPE.ME);
const activeStatus = ref(wootConstants.STATUS_TYPE.ALL); // Filtro padrão: Todas
const activeSortBy = ref(wootConstants.SORT_BY_TYPE.LAST_ACTIVITY_AT_DESC);
const viewMode = ref('conversations');
const crmFilters = ref(emptyPipelineFilters());
const crmConversations = ref([]);
const crmTimezone = ref('');
const crmLoadError = ref(false);
let crmRequestId = 0;
let crmFilterTimer;
const crmSummary = ref({});
const isLoadingCrmSummary = ref(false);

// Getters nativos do store
const mineChatsList = useMapGetter('getMineChats');
const allChatList = useMapGetter('getAllStatusChats');
const unAssignedChatsList = useMapGetter('getUnAssignedChats');
const chatLists = useMapGetter('getFilteredConversations');
const currentUser = useMapGetter('getCurrentUser');
const currentAccountId = useMapGetter('getCurrentAccountId');
const appliedFilters = useMapGetter('getAppliedConversationFilters');
const accountAgents = useMapGetter('agents/getAgents');

const userPermissions = computed(() =>
  getUserPermissions(currentUser.value, currentAccountId.value)
);

const TAB_NAMES = {
  [wootConstants.ASSIGNEE_TYPE.ALL]: 'Todas',
  [wootConstants.ASSIGNEE_TYPE.ME]: 'Minhas',
  [wootConstants.ASSIGNEE_TYPE.UNASSIGNED]: 'Não atribuídas',
};
const getCrmTabName = key => {
  if (key === wootConstants.ASSIGNEE_TYPE.ME) {
    return t('KANBAN_VIEW.CRM.MY_DEALS');
  }
  if (key === wootConstants.ASSIGNEE_TYPE.UNASSIGNED) {
    return t('KANBAN_VIEW.CRM.UNASSIGNED_DEALS');
  }
  return t('KANBAN_VIEW.CRM.ALL_DEALS');
};

// As abas seguem a MESMA regra de permissão da lista de conversas
// (ASSIGNEE_TYPE_TAB_PERMISSIONS). Antes o Kanban montava as três na mão, então
// uma função personalizada sem "gerenciar todas as conversas" continuava vendo
// a aba Todas aqui — só que com os dados já recortados pelo backend.
const allowedAssigneeTabs = computed(() =>
  filterItemsByPermission(
    ASSIGNEE_TYPE_TAB_PERMISSIONS,
    userPermissions.value,
    item => item.permissions
  )
    .map(({ key }) => key)
    .filter(key => TAB_NAMES[key])
);

// Composables de permissão
const { isAdmin } = useAdmin();

// Verificar se é super admin
const isSuperAdmin = computed(() => {
  return currentUser.value?.type === 'SuperAdmin';
});

// Aba inicial: "Todas" para quem pode vê-la; quem não pode cai na primeira aba
// permitida — sem isso o Kanban abria numa aba que nem existe mais no seletor e
// ficava sem nada selecionado.
watch(
  [isSuperAdmin, isAdmin, allowedAssigneeTabs],
  () => {
    const tabs = allowedAssigneeTabs.value;
    activeAssigneeTab.value = tabs.includes(wootConstants.ASSIGNEE_TYPE.ALL)
      ? wootConstants.ASSIGNEE_TYPE.ALL
      : tabs[0] || wootConstants.ASSIGNEE_TYPE.ME;
  },
  { immediate: true }
);

// Helper para obter ícone do canal (moved before use)
const getChannelIcon = (channelType, provider) => {
  const channelTypeMap = {
    'Channel::Api': 'ri-cloudy-fill',
    'Channel::Email': 'ri-mail-fill',
    'Channel::FacebookPage': 'ri-messenger-fill',
    'Channel::Line': 'ri-line-fill',
    'Channel::Sms': 'ri-chat-1-fill',
    'Channel::Telegram': 'ri-telegram-fill',
    'Channel::TwilioSms': 'ri-chat-1-fill',
    'Channel::TwitterProfile': 'ri-twitter-x-fill',
    'Channel::WebWidget': 'ri-global-fill',
    'Channel::Whatsapp': 'ri-whatsapp-fill',
    'Channel::Instagram': 'ri-instagram-fill',
    'Channel::Voice': 'ri-phone-fill',
    api: 'ri-cloudy-fill',
    email: 'ri-mail-fill',
    facebook: 'ri-messenger-fill',
    line: 'ri-line-fill',
    sms: 'ri-chat-1-fill',
    telegram: 'ri-telegram-fill',
    twilio: 'ri-chat-1-fill',
    twitter: 'ri-twitter-x-fill',
    web_widget: 'ri-global-fill',
    whatsapp: 'ri-whatsapp-fill',
    instagram: 'ri-instagram-fill',
    voice: 'ri-phone-fill',
  };

  // Se for email, verificar provider
  if (
    (channelType === 'Channel::Email' || channelType === 'email') &&
    provider
  ) {
    if (provider === 'microsoft') return 'ri-microsoft-fill';
    if (provider === 'google') return 'ri-google-fill';
  }

  return channelTypeMap[channelType] || 'ri-global-fill';
};

// Helper para obter cor do canal (moved before use)
const getChannelColor = icon => {
  if (icon.includes('whatsapp')) return 'text-green-500';
  if (icon.includes('messenger') || icon.includes('facebook'))
    return 'text-blue-500';
  if (icon.includes('instagram')) return 'text-pink-500';
  if (icon.includes('telegram')) return 'text-cyan-500';
  if (icon.includes('twitter')) return 'text-sky-500';
  if (icon.includes('mail')) return 'text-amber-500';
  if (icon.includes('microsoft')) return 'text-blue-600';
  if (icon.includes('google')) return 'text-red-500';
  if (icon.includes('phone')) return 'text-purple-500';
  return 'text-slate-500';
};

// Composables - usar selectedFunnelId do composable (moved before use)
const { funnels, currentFunnel, selectedFunnelId, loadFunnels, selectFunnel } =
  useFunnelManager();

// Bug #3 Fix: Mutex lock para prevenir race conditions
const { acquireLock, isLocked } = useMutexLock();

// Bug #16 & #19 Fix: Storage com TTL, cleanup automático e lock para prevenir race conditions
const kanbanStorage = useKanbanStorage();

// Filtros da conversa usando APENAS parâmetros nativos do Chatwoot (moved before use)
const conversationFilters = computed(() => {
  return {
    inboxId:
      props.inboxId && props.inboxId !== 0
        ? parseInt(props.inboxId, 10)
        : undefined,
    assigneeType:
      viewMode.value === 'crm'
        ? wootConstants.ASSIGNEE_TYPE.ALL
        : activeAssigneeTab.value,
    status:
      viewMode.value === 'crm'
        ? wootConstants.STATUS_TYPE.ALL
        : activeStatus.value,
    sortBy: activeSortBy.value,
    page: 1,
    // Kanban needs all conversations with funnel_mappings loaded
    disablePagination: true,
    // PERF: Filtra no backend para retornar APENAS conversas do funil ativo
    // em vez de todas as conversas da conta (3-5s → muito menos).
    funnelId: selectedFunnelId.value || undefined,
  };
});

// Verifica se tem filtros aplicados
const hasAppliedFilters = computed(() => {
  return appliedFilters.value && appliedFilters.value.length > 0;
});

const mappingForCurrentFunnel = conversation =>
  (conversation.funnel_mappings || []).find(
    mapping =>
      String(mapping.funnel_id) === String(currentFunnel.value?.id || '')
  );

// Lista de conversas usando a lógica NATIVA do Chatwoot (moved before use)
const conversationList = computed(() => {
  if (viewMode.value === 'crm') return crmConversations.value;
  let localConversationList = [];

  if (!hasAppliedFilters.value) {
    // Usar filtros básicos nativos do Chatwoot
    const filters = conversationFilters.value;

    if (activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.ME) {
      localConversationList = [...mineChatsList.value(filters)];
    } else if (
      activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.UNASSIGNED
    ) {
      localConversationList = [...unAssignedChatsList.value(filters)];
    } else if (activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.ALL) {
      localConversationList = [...allChatList.value(filters)];
    }
  } else {
    // Usar filtros avançados aplicados
    localConversationList = [...chatLists.value];
  }

  // Filtrar por funil atual usando APENAS funnel_mappings (fonte única de verdade)
  if (currentFunnel.value) {
    const funnelId = currentFunnel.value.id;
    localConversationList = localConversationList.filter(c => {
      return (
        c.funnel_mappings &&
        c.funnel_mappings.length > 0 &&
        c.funnel_mappings.some(
          mapping => String(mapping.funnel_id) === String(funnelId)
        )
      );
    });
  }

  return localConversationList;
});

// Computed para obter canais únicos das conversas atuais (moved after conversationList)
const activeChannels = computed(() => {
  const channels = new Map();
  const inboxes = store.getters['inboxes/getInboxes'] || [];

  // Mostrar todos os canais das conversas no funil, sem restrição
  conversationList.value.forEach(conv => {
    const inbox = inboxes.find(i => i.id === conv.inbox_id);
    if (inbox) {
      const channelType = inbox.channel_type || inbox.channel;
      const icon = getChannelIcon(channelType, inbox.provider);
      const color = getChannelColor(icon);

      if (!channels.has(channelType)) {
        channels.set(channelType, {
          type: channelType,
          icon,
          color,
          count: 0,
          inboxes: [],
        });
      }

      const channel = channels.get(channelType);
      channel.count += 1;

      if (!channel.inboxes.includes(inbox.name)) {
        channel.inboxes.push(inbox.name);
      }
    }
  });

  return Array.from(channels.values());
});

// "Um card por contato no board" (funnel.settings.board_one_card_per_contact):
// colapsa as várias conversas do mesmo contato num card só, representado pela
// conversa mais relevante — aberta > adiada > pendente > resolvida; no empate,
// a mais recente. Resolvidas antigas somem do board (seguem no histórico e nos
// relatórios). Sem contato identificável, mantém o card (não some da tela).
const CONTACT_DEDUPE_STATUS_RANK = {
  open: 0,
  snoozed: 1,
  pending: 2,
  resolved: 3,
};
const contactKeyOf = c => c.contact?.id ?? c.meta?.sender?.id ?? null;
const conversationRecencyOf = c => {
  const raw = c.last_activity_at || c.created_at || 0;
  return typeof raw === 'number' ? raw : new Date(raw).getTime() || 0;
};
const dedupeConversationsByContact = list => {
  const best = new Map();
  list.forEach(c => {
    const key = contactKeyOf(c);
    if (key === null || key === undefined) {
      best.set(`conv-${c.id}`, c);
      return;
    }
    const current = best.get(key);
    if (!current) {
      best.set(key, c);
      return;
    }
    const rankNew = CONTACT_DEDUPE_STATUS_RANK[c.status] ?? 9;
    const rankCur = CONTACT_DEDUPE_STATUS_RANK[current.status] ?? 9;
    if (
      rankNew < rankCur ||
      (rankNew === rankCur &&
        conversationRecencyOf(c) > conversationRecencyOf(current))
    ) {
      best.set(key, c);
    }
  });
  return [...best.values()];
};
const oneCardPerContactEnabled = () =>
  currentFunnel.value?.settings?.board_one_card_per_contact === true;

// Computed para contar conversas por tab no funil atual
const getFunnelFilteredCount = type => {
  if (!currentFunnel.value) {
    return 0;
  }

  const funnelId = currentFunnel.value.id;
  const filters = conversationFilters.value;

  let conversations = [];
  if (type === wootConstants.ASSIGNEE_TYPE.ME) {
    conversations = mineChatsList.value(filters);
  } else if (type === wootConstants.ASSIGNEE_TYPE.UNASSIGNED) {
    conversations = unAssignedChatsList.value(filters);
  } else if (type === wootConstants.ASSIGNEE_TYPE.ALL) {
    conversations = allChatList.value(filters);
  }

  // Filtrar por funil usando APENAS funnel_mappings (fonte única de verdade)
  const filtered = conversations.filter(c => {
    return (
      c.funnel_mappings &&
      c.funnel_mappings.length > 0 &&
      c.funnel_mappings.some(
        mapping => String(mapping.funnel_id) === String(funnelId)
      )
    );
  });

  // Com "um card por contato" ligado, a contagem também colapsa por contato
  // para bater com o board.
  const counted =
    viewMode.value !== 'crm' && oneCardPerContactEnabled()
      ? dedupeConversationsByContact(filtered)
      : filtered;

  return counted.length;
};

const assigneeTabItems = computed(() =>
  allowedAssigneeTabs.value.map(key => ({
    key,
    name: viewMode.value === 'crm' ? getCrmTabName(key) : TAB_NAMES[key],
    count: getFunnelFilteredCount(key),
  }))
);

// Bug #7 Fix: Use Vuex as single source of truth
// modifiedStages is now ONLY a temporary optimistic update cache
// All persistent data is in funnel_mappings from backend
const modifiedStages = ref({});

// Busca de leads no pipeline (filtra os cards por nome/telefone/email do contato)
const searchQuery = ref('');
const crmQuery = computed(() => ({
  ...pipelineParams(crmFilters.value),
  inbox_id: props.inboxId || undefined,
  search: searchQuery.value || undefined,
}));

const exportDialogRef = ref(null);
const isExportingLeads = ref(false);
const exportDealStatus = ref('all');
const exportStageNames = ref([]);
const includeCustomAttributes = ref(true);

const exportStageOptions = computed(() => {
  if (!currentFunnel.value?.stages) return [];
  return Object.entries(currentFunnel.value.stages)
    .map(([name, config]) => ({
      name,
      label: config.label || name,
      position: config.position || 0,
    }))
    .sort((a, b) => a.position - b.position);
});

const openExportDialog = () => {
  exportStageNames.value =
    viewMode.value === 'crm' && crmFilters.value.stage_names.length
      ? [...crmFilters.value.stage_names]
      : exportStageOptions.value.map(stage => stage.name);
  exportDealStatus.value =
    viewMode.value === 'crm' ? crmFilters.value.deal_status : 'all';
  exportDialogRef.value?.open();
};

const exportLeads = async () => {
  if (!currentFunnel.value) return;

  isExportingLeads.value = true;
  try {
    const response = await KanbanAPI.exportLeads(currentFunnel.value.id, {
      stage_names: exportStageNames.value.join(','),
      deal_status: exportDealStatus.value,
      assignee_id: activeAssigneeTab.value,
      status: activeStatus.value,
      inbox_id: props.inboxId || undefined,
      search: searchQuery.value || undefined,
      include_custom_attributes: includeCustomAttributes.value,
      ...(viewMode.value === 'crm' ? { ...crmQuery.value, crm: true } : {}),
    });
    const disposition = response.headers['content-disposition'] || '';
    const fileName =
      disposition.match(/filename="?([^";]+)"?/i)?.[1] ||
      `leads-pipeline-${currentFunnel.value.id}.csv`;
    const url = URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(url);
    exportDialogRef.value?.close();
    useAlert(t('KANBAN_VIEW.EXPORT.SUCCESS'));
  } catch (error) {
    useAlert(t('KANBAN_VIEW.EXPORT.ERROR'));
  } finally {
    isExportingLeads.value = false;
  }
};
// Recolhida em lupa; clique abre o popover com o input (fecha no clickaway se vazio)
const searchOpen = ref(false);
const searchInputRef = ref(null);
const openSearch = async () => {
  searchOpen.value = true;
  await nextTick();
  searchInputRef.value?.focus();
};
const closeSearchIfEmpty = () => {
  if (!searchQuery.value.trim()) searchOpen.value = false;
};
const clearSearch = () => {
  searchQuery.value = '';
  searchOpen.value = false;
};

// Instante local do último drag por conversa: mantém o card recém-arrastado no
// topo da coluna já no gesto, antes de o stage_entered_at novo voltar do server.
const modifiedStageTimes = ref({});

// Track which conversations have pending optimistic updates
const optimisticUpdates = new Map();

// Bug #7 & #16 & #19 Fix: Funções auxiliares para persistência refatoradas
const loadSavedStages = () => {
  const accountId = store.getters.getCurrentAccountId;
  if (!accountId) return {};

  // Bug #19 Fix: Usar useKanbanStorage com TTL e cleanup
  const saved = kanbanStorage.getItem('conversation_stages', accountId);
  return saved || {};
};

const saveStages = async stages => {
  // Bug #16 Fix: Usar mutex lock para prevenir race condition no localStorage
  const release = await acquireLock('saveStages', 5000);

  try {
    const accountId = store.getters.getCurrentAccountId;
    if (!accountId) return;

    // Carregar stages existentes de TODOS os funis
    const existingStages = loadSavedStages();

    // Atualizar apenas os stages do funil atual
    const enrichedStages = { ...existingStages };

    Object.entries(stages).forEach(([convId, stage]) => {
      enrichedStages[convId] = {
        stage,
        funnel_id: currentFunnel.value?.id,
        timestamp: Date.now(),
        updated_at: new Date().toISOString(),
      };
    });

    // Bug #19 Fix: Usar useKanbanStorage com TTL (7 dias)
    kanbanStorage.setItem('conversation_stages', enrichedStages, accountId);

    // Bug #19 Fix: Executar cleanup periódico
    kanbanStorage.cleanupOldData(accountId);
  } finally {
    // Bug #16 Fix: Sempre liberar lock
    release();
  }
};

// Computed para calcular o valor total geral de todas as conversas visíveis DO FUNIL ATUAL
const totalGeneralValue = computed(() => {
  let total = 0;
  const currentFunnelId = currentFunnel.value?.id;

  // Se não há funil selecionado, retorna 0
  if (!currentFunnelId) return 0;

  conversationList.value.forEach(conversation => {
    // Prioridade 1: Valor do deal_value do mapping (definido inline no Kanban)
    if (
      conversation.funnel_mappings &&
      conversation.funnel_mappings.length > 0
    ) {
      const mapping = conversation.funnel_mappings.find(
        m => String(m.funnel_id) === String(currentFunnelId)
      );
      if (mapping && mapping.deal_value != null && mapping.deal_value !== '') {
        total += parseFloat(mapping.deal_value) || 0;
        return; // Não somar outras fontes se já tem deal_value
      }
    }

    // Prioridade 2: Valor do Deal vinculado à conversa (se existir e for do mesmo funil)
    const deal = conversation.deal;
    if (deal && deal.amount) {
      if (
        deal.funnel_id === currentFunnelId ||
        String(deal.funnel_id) === String(currentFunnelId)
      ) {
        total += parseFloat(deal.amount) || 0;
        return; // Não somar produtos se já tem deal
      }
    }

    // Prioridade 3: Somar produtos da conversa
    const products = conversation.conversation_products || [];

    // Filtrar produtos pelo funnel_id atual
    // Incluir produtos sem funnel_id (null) ou do funil atual
    const filteredProducts = products.filter(
      product => !product.funnel_id || product.funnel_id === currentFunnelId
    );

    filteredProducts.forEach(product => {
      total += parseFloat(product.amount) || 0;
    });
  });

  return total;
});

// Formatar valor total geral
const formattedTotalGeneralValue = computed(() => {
  return new Intl.NumberFormat(browserLocale.value, {
    style: 'currency',
    currency: currency.value,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(totalGeneralValue.value);
});

const formatCrmValue = value =>
  new Intl.NumberFormat(browserLocale.value, {
    style: 'currency',
    currency: currency.value,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value) || 0);

const crmStorageKey = computed(
  () =>
    `crm_pipeline_views:${currentAccountId.value}:${currentUser.value?.id}:${selectedFunnelId.value}`
);
const fetchCrmSummary = async () => {
  crmRequestId += 1;
  const requestId = crmRequestId;
  if (viewMode.value !== 'crm' || !selectedFunnelId.value) return;
  const funnelId = selectedFunnelId.value;
  const query = { ...crmQuery.value };
  isLoadingCrmSummary.value = true;
  crmLoadError.value = false;
  try {
    const rows = [];
    let summary;
    let page = 1;
    let hasMore = true;
    while (hasMore) {
      // Each page depends on the previous response and can be cancelled by a new filter.
      // eslint-disable-next-line no-await-in-loop
      const { data } = await KanbanAPI.getCrmSummary(funnelId, {
        ...query,
        include_cards: true,
        include_summary: page === 1,
        page,
      });
      if (requestId !== crmRequestId) return;
      if (page === 1) summary = data;
      rows.push(...data.conversations);
      hasMore = data.meta.has_next_page;
      page += 1;
    }
    crmConversations.value = rows;
    crmSummary.value = summary;
    crmTimezone.value = summary.timezone;
  } catch {
    if (requestId !== crmRequestId) return;
    crmConversations.value = [];
    crmSummary.value = {};
    crmLoadError.value = true;
    useAlert(t('KANBAN_VIEW.CRM.SUMMARY_ERROR'));
  } finally {
    if (requestId === crmRequestId) isLoadingCrmSummary.value = false;
  }
};

const setViewMode = mode => {
  viewMode.value = mode;
  localStorage.setItem('kanban_view_mode', mode);
  if (mode === 'crm') {
    activeStatus.value = wootConstants.STATUS_TYPE.ALL;
    showAdvancedFilters.value = false;
  }
};

const crmStatusLabel = computed(() => {
  const key =
    { open: 'OPEN', won: 'WON', lost: 'LOST' }[crmFilters.value.deal_status] ||
    'ALL_STATUS';
  return t(`KANBAN_VIEW.CRM.${key}`);
});

// Computed para conversas com todos os filtros aplicados
const conversations = computed(() => {
  // Usar conversationList que já aplica todos os filtros necessários
  let filteredConversations = conversationList.value;

  // Adicionar informações do funil às conversas
  if (currentFunnel.value && currentFunnel.value.id) {
    filteredConversations = filteredConversations.map(c => {
      // Verificar se este conversa teve seu stage modificado
      const modifiedStage = modifiedStages.value[c.id];

      // Buscar o stage_name do mapping específico para este funil
      let stageName = null;

      // Dados do mapping para este funil
      let dealValue = null;
      let dealStatus = null;
      let stageEnteredAt = null;
      let timerData = null;
      let checklist = null;
      let mappingId = null;

      // Primeiro tenta buscar dos funnel_mappings (múltiplos funis)
      if (c.funnel_mappings && c.funnel_mappings.length > 0) {
        const mapping = c.funnel_mappings.find(
          m => String(m.funnel_id) === String(currentFunnel.value.id)
        );
        if (mapping) {
          stageName = mapping.stage_name;
          dealValue = mapping.deal_value;
          dealStatus = mapping.deal_status;
          stageEnteredAt = mapping.stage_entered_at;
          timerData = mapping.timer_data;
          checklist = mapping.checklist;
          mappingId = mapping.id;
        }
      }

      // Aplica modificações locais ou usa o stage encontrado ou default
      stageName =
        modifiedStage ||
        stageName ||
        Object.keys(currentFunnel.value.stages)[0];

      return {
        ...c,
        funnel_id: currentFunnel.value.id,
        funnel: currentFunnel.value, // Adicionar o objeto funnel completo para os cards terem acesso às configurações
        stage_name: stageName,
        deal_value: dealValue,
        deal_status: dealStatus,
        stage_entered_at: stageEnteredAt,
        timer_data: timerData,
        checklist: checklist,
        mapping_id: mappingId,
      };
    });
  }

  // Se tiver inbox selecionado adicionalmente, filtrar por inbox
  if (props.inboxId && props.inboxId !== 0) {
    filteredConversations = filteredConversations.filter(
      c => c.inbox_id === parseInt(props.inboxId, 10)
    );
  }

  // Busca por lead: nome, telefone, email ou empresa do contato
  const query = searchQuery.value.trim().toLowerCase();
  if (query && viewMode.value !== 'crm') {
    filteredConversations = filteredConversations.filter(c => {
      const contact = c.contact || c.meta?.sender || {};
      return [
        contact.name,
        contact.phone_number,
        contact.email,
        contact.company_name,
      ].some(field =>
        String(field || '')
          .toLowerCase()
          .includes(query)
      );
    });
  }

  // Aplicar ordenação se especificada
  if (activeSortBy.value && activeSortBy.value !== 'last_activity_at_desc') {
    filteredConversations = [...filteredConversations].sort((a, b) => {
      const priorityOrder = { urgent: 0, high: 1, medium: 2, low: 3 };

      switch (activeSortBy.value) {
        case 'priority_desc': {
          return (
            (priorityOrder[a.priority] || 999) -
            (priorityOrder[b.priority] || 999)
          );
        }
        case 'created_at_desc': {
          return new Date(b.created_at) - new Date(a.created_at);
        }
        default: {
          return (
            new Date(b.last_activity_at || b.created_at) -
            new Date(a.last_activity_at || a.created_at)
          );
        }
      }
    });
  }

  return filteredConversations;
});

// Removido - funis não dependem mais de inboxes
// const hasConfiguredInboxes = computed(() => {
//   const inboxIds = currentFunnel.value?.settings?.inbox_ids || [];
//   return inboxIds.length > 0;
// });

// Stages array for DealDetailsSidebar
const stagesArray = computed(() => {
  if (!currentFunnel.value?.stages) return [];
  return Object.entries(currentFunnel.value.stages)
    .map(([key, config]) => ({
      name: key,
      label: config.label || key,
      color: config.color,
      position: config.position || 0,
    }))
    .sort((a, b) => a.position - b.position);
});

const groupedConversations = computed(() => {
  if (!currentFunnel.value) return {};

  const grouped = {};
  // Inicializar todos os estágios com arrays vazios
  Object.keys(currentFunnel.value.stages).forEach(stage => {
    grouped[stage] = [];
  });

  // Agrupar conversas por estágio ESPECÍFICO DO FUNIL ATUAL
  const funnelId = currentFunnel.value.id;
  const firstStage = Object.keys(currentFunnel.value.stages)[0];
  const seenIds = new Set();

  const source =
    viewMode.value !== 'crm' && oneCardPerContactEnabled()
      ? dedupeConversationsByContact(conversations.value)
      : conversations.value;

  source.forEach(conversation => {
    // Prevenir duplicatas
    if (seenIds.has(conversation.id)) return;
    seenIds.add(conversation.id);
    let stage = null;

    // 1. Mudanças otimistas (drag & drop em andamento) - PRIORIDADE MÁXIMA
    if (modifiedStages.value[conversation.id]) {
      stage = modifiedStages.value[conversation.id];
    }

    // 2. funnel_mappings do banco (fonte de verdade)
    if (!stage && conversation.funnel_mappings) {
      const mapping = conversation.funnel_mappings.find(
        m => String(m.funnel_id) === String(funnelId)
      );
      if (mapping && mapping.stage_name) {
        stage = mapping.stage_name;
      }
    }

    // 3. Fallback: primeiro stage do funil
    if (!stage) {
      stage = firstStage;
    }

    // Adicionar ao grupo correto (se stage não existe no funil, vai pro primeiro)
    if (grouped[stage]) {
      grouped[stage].push(conversation);
    } else if (firstStage) {
      grouped[firstStage].push(conversation);
    }
  });

  // Ordem da coluna = chegada na etapa (mais recente no topo): quem acabou de
  // ser arrastado fica no topo. Sort explícito do usuário (prioridade etc.)
  // preserva a ordem global já aplicada em `conversations`.
  if (activeSortBy.value === 'last_activity_at_desc') {
    const arrivalTs = conversation => {
      const local = modifiedStageTimes.value[conversation.id] || 0;
      const mapping = (conversation.funnel_mappings || []).find(
        m => String(m.funnel_id) === String(funnelId)
      );
      const raw = mapping?.stage_entered_at || conversation.created_at || 0;
      const parsed =
        typeof raw === 'number' ? raw * 1000 : new Date(raw).getTime() || 0;
      return Math.max(local, parsed);
    };
    Object.keys(grouped).forEach(stage => {
      grouped[stage] = [...grouped[stage]].sort(
        (a, b) => arrivalTs(b) - arrivalTs(a)
      );
    });
  }

  return grouped;
});

// Methods
const handleFunnelSelection = async funnelId => {
  if (!funnelId) {
    return;
  }

  // Evitar recarregar o mesmo funil
  if (String(selectedFunnelId.value) === String(funnelId)) {
    return;
  }

  isLoadingFunnel.value = true;

  try {
    // Limpar stages modificados ao trocar de funil
    modifiedStages.value = {};

    // Usar o método selectFunnel do composable
    await selectFunnel(funnelId);

    // F5 fix: refletir o funil ativo na URL para que reload preserve o estado.
    // Usar replace para não poluir o histórico de back/forward.
    if (String(route.params.funnelId) !== String(funnelId)) {
      router.replace({
        name: 'pipeline_funnel_view',
        params: { accountId: route.params.accountId, funnelId },
        query: route.query,
      });
    }

    // Aguardar um tick para garantir que o currentFunnel foi atualizado
    await nextTick();

    // Verificar se o funil foi carregado corretamente
    if (currentFunnel.value) {
      // PERSISTENCE FIX: Clear modifiedStages when switching funnels
      // API data (funnel_mappings) is the source of truth
      modifiedStages.value = {};

      // Forçar recarregamento das conversas
      await store.dispatch('fetchAllConversations');
    }
  } catch {
    // Error handled silently
  } finally {
    isLoadingFunnel.value = false;
  }
};

// Deal-rich T6: clicar no card abre o registro do negócio (rota
// pipeline_deal) em vez do modal de conversa. Preserva o funil atual na
// query para o DealHeader/back button voltarem ao mesmo lugar.
const handleCardClick = async conversationId => {
  // Overlay pinta ANTES do mount pesado da tela do negócio travar a thread —
  // sem isso o clique dava segundos de tela congelada sem feedback.
  showScreenTransitionOverlay(t('KANBAN.OPENING_DEAL'));
  await paintThen();
  router.push({
    name: 'pipeline_deal',
    params: { conversationId },
    query: currentFunnel.value?.id ? { funnelId: currentFunnel.value.id } : {},
  });
};

const openConversationModal = conversation => {
  if (conversation && conversation.id) {
    selectedConversationId.value = conversation.id;
    showConversationModal.value = true;
  }
};

// Controle de tentativas para evitar loops infinitos (moved before use)
const fetchAttempts = ref(0);
const maxFetchAttempts = 3;
const fetchTimeout = ref(null);

// Ref para controlar chamadas de fetchConversations (moved before use)
const isFetching = ref(false);
// Skeleton do board: true enquanto o 1º fetch das conversas roda (sem dados
// ainda) — antes o board aparecia VAZIO por segundos e o usuário ficava perdido.
const isFetchingBoard = ref(false);

// Debounce timer (moved before use)
let fetchDebounceTimer = null;

// Bug #3 Fix: fetchConversations com mutex lock para prevenir race conditions (moved before use)
const fetchConversations = async (forceRefresh = false) => {
  if (viewMode.value === 'crm') {
    await fetchCrmSummary();
    return;
  }
  // Proteção contra loops infinitos
  if (fetchAttempts.value >= maxFetchAttempts) {
    fetchAttempts.value = 0; // Reset para permitir tentativas futuras
    return;
  }

  // Bug #3 Fix: Usar mutex lock ao invés de isFetching simples
  if (isLocked('fetchConversations')) {
    // Já existe uma operação em andamento, retornar
    return;
  }

  // Adquirir lock (com timeout de 30 segundos)
  const release = await acquireLock('fetchConversations', 30000);

  fetchAttempts.value += 1;

  try {
    isFetchingBoard.value = true;
    // Bug #20 & #21 Fix: Force refresh clears cache and ensures complete data
    if (forceRefresh) {
      // Clear all conversations to force fresh fetch from backend
      await store.dispatch('emptyAllConversations');
    }

    const filters = conversationFilters.value;
    await store.dispatch('setChatListFilters', filters);
    await store.dispatch('fetchAllConversations');

    fetchAttempts.value = 0; // Reset contador em caso de sucesso
  } catch (error) {
    // Se for erro 500, não tentar novamente automaticamente
    if (error.response && error.response.status === 500) {
      fetchAttempts.value = maxFetchAttempts; // Previne novas tentativas
    }
  } finally {
    isFetchingBoard.value = false;
    // Bug #3 Fix: Sempre liberar o lock
    release();
  }
};

// Skeleton só quando ainda não há NADA na tela (refetch silencioso não pisca).
const showBoardSkeleton = computed(
  () =>
    (viewMode.value === 'crm'
      ? isLoadingCrmSummary.value
      : isFetchingBoard.value) && conversationList.value.length === 0
);

// Definir handleLeadCreated agora que fetchConversations está disponível
handleLeadCreated = async () => {
  await fetchConversations();
};

// Handlers de filtros nativos (moved after fetchConversations)
const onBasicFilterChange = (value, type) => {
  if (type === 'status') {
    activeStatus.value = value;
  } else if (type === 'sort') {
    activeSortBy.value = value;
  }

  // Buscar conversas com os novos filtros
  // Os filtros serão automaticamente incluídos via conversationFilters computed
  fetchConversations();
};

const updateAssigneeTab = selectedTab => {
  if (activeAssigneeTab.value !== selectedTab) {
    activeAssigneeTab.value = selectedTab;
  }
};

const handleFiltersModal = () => {
  showAdvancedFilters.value = true;
  // Forçar atualização do DOM
  nextTick(() => {});
};

const closeAdvanceFiltersModal = () => {
  showAdvancedFilters.value = false;
};

const onApplyFilter = filter => {
  appliedFilter.value = filter;

  // Criar payload com filtros - garantir que seja array
  let payload = [];
  if (Array.isArray(filter)) {
    payload = [...filter];
  } else if (filter) {
    payload = [filter];
  }

  // Removido - funis não filtram mais por inbox
  // As conversas são adicionadas manualmente ao funil

  // Garantir que o último item não tenha query_operator ou seja null
  if (payload.length > 0) {
    payload[payload.length - 1].query_operator = null;
  }

  // Converter para snake_case como o ConversationFilter original faz
  const snakeCasePayload = useSnakeCase(payload);

  // Aplicar filtros usando a mesma lógica do ConversationFilter original (sem namespace)
  store.dispatch('setChatListFilters', {
    appliedFilters: snakeCasePayload,
  });

  // Buscar conversas filtradas
  store
    .dispatch('fetchFilteredConversations', {
      queryData: snakeCasePayload,
      page: 1,
    })
    .then(() => {})
    .catch(() => {
      // Em caso de erro, limpar filtros e buscar todas
      store.dispatch('setChatListFilters', {});
      fetchConversations();
    });

  showAdvancedFilters.value = false;
};

// Reset filters function (commented out but kept for potential future use)
// const resetFilters = () => {
//   appliedFilter.value = [];
//   activeStatus.value = wootConstants.STATUS_TYPE.OPEN;
//   activeSortBy.value = wootConstants.SORT_BY_TYPE.LAST_ACTIVITY_AT_DESC;
//
//   store.dispatch('setChatListFilters', {});
//   fetchConversations();
// };

// Win/Lose Deal handlers
const handleWinDeal = ({ conversation, mapping, currentValue }) => {
  selectedDealConversation.value = conversation;
  selectedDealMapping.value = mapping;
  selectedDealValue.value = currentValue || 0;
  showWinDealModal.value = true;
};

const handleLoseDeal = ({ conversation, mapping }) => {
  selectedDealConversation.value = conversation;
  selectedDealMapping.value = mapping;
  showLoseDealModal.value = true;
};

// Considera apenas o nome visível. Chaves técnicas antigas como closed_lost
// continuam existindo mesmo quando a coluna foi renomeada para "No Show".
const terminalStageKind = stageKey =>
  sharedTerminalStageKind(currentFunnel.value, stageKey);

// Depois do fechamento confirmado, leva o card para a coluna onde o usuário
// soltou (o servidor só move quando a CHAVE da etapa casa com a heurística).
const applyPendingTerminalMove = async conversationId => {
  const pending = pendingTerminalMove.value;
  pendingTerminalMove.value = null;
  if (!pending || pending.conversationId !== conversationId) return;
  try {
    await KanbanAPI.moveConversation(
      currentFunnel.value.id,
      conversationId,
      pending.stageKey
    );
  } catch (error) {
    // O fechamento já aconteceu; a etapa fica onde o servidor decidiu.
  }
};

// Bug #4-5 Fix: handleCardMove com mutex lock e fila de operações
const handleCardMove = async ({ conversationId, fromStage, toStage }) => {
  // Verificar se toStage é uma key válida
  const isValidKey = Object.prototype.hasOwnProperty.call(
    currentFunnel.value.stages,
    toStage
  );

  // Se não for uma key válida, pode ser uma label - vamos tentar converter
  let actualStageKey = toStage;
  if (!isValidKey) {
    // Procurar a key pela label
    const entry = Object.entries(currentFunnel.value.stages).find(
      ([, config]) => {
        return config.label === toStage || config.name === toStage;
      }
    );

    if (entry) {
      actualStageKey = entry[0];
    } else {
      // Bug #25 Fix: Exibir erro ao usuário
      useAlert(t('KANBAN.ERRORS.INVALID_STAGE'));
      return;
    }
  }

  // Verificar se é o mesmo estágio (comparar keys)
  let actualFromKey = fromStage;
  if (
    !Object.prototype.hasOwnProperty.call(currentFunnel.value.stages, fromStage)
  ) {
    const fromEntry = Object.entries(currentFunnel.value.stages).find(
      ([, config]) => {
        return config.label === fromStage || config.name === fromStage;
      }
    );
    if (fromEntry) {
      actualFromKey = fromEntry[0];
    }
  }

  if (actualFromKey === actualStageKey) {
    return;
  }

  // Etapa terminal: arrastar para "Ganho/Perdido" com negócio em outro status
  // (aberto, ou perdido indo para Ganho e vice-versa) não move nada — abre o
  // fechamento, como os seletores da conversa. Sem isto o card ficava na
  // coluna de ganho com deal_status open (ou o servidor recusava sem explicar).
  const terminalKind = terminalStageKind(actualStageKey);
  if (terminalKind) {
    const conv = conversationList.value.find(c => c.id === conversationId);
    const mapping = conv?.funnel_mappings?.find(
      m => String(m.funnel_id) === String(currentFunnel.value.id)
    );
    if (mapping && (mapping.deal_status || 'open') !== terminalKind) {
      pendingTerminalMove.value = { conversationId, stageKey: actualStageKey };
      if (terminalKind === 'won') {
        handleWinDeal({
          conversation: conv,
          mapping,
          currentValue: mapping.deal_value || 0,
        });
      } else {
        handleLoseDeal({ conversation: conv, mapping });
      }
      return;
    }
  }

  // Bug #4-5 Fix: Adquirir lock para prevenir conflitos com WebSocket
  const lockKey = `cardMove_${conversationId}`;
  if (isLocked(lockKey)) {
    // Operação já em andamento para este card
    return;
  }

  const release = await acquireLock(lockKey, 10000);

  try {
    // Verificar se o usuário pode mover conversas neste funil
    if (currentFunnel.value?.settings?.allow_manual_move === false) {
      useAlert(t('KANBAN.ERRORS.MANUAL_MOVE_NOT_ALLOWED'));
      return;
    }

    // PERFORMANCE: Track optimistic update (minimal data)
    optimisticUpdates.set(conversationId, {
      previousStage: modifiedStages.value[conversationId] || actualFromKey,
    });

    // INSTANT: Update local state immediately
    // FIX: Create new object to ensure Vue reactivity triggers properly
    modifiedStages.value = {
      ...modifiedStages.value,
      [conversationId]: actualStageKey,
    };
    modifiedStageTimes.value = {
      ...modifiedStageTimes.value,
      [conversationId]: Date.now(),
    };

    // ASYNC: Save to localStorage (non-blocking)
    saveStages({ [conversationId]: actualStageKey });

    // PERSISTENCE FIX: Await the API response to ensure persistence
    // The optimistic update already shows the card in the new position
    try {
      const response = await KanbanAPI.moveConversation(
        currentFunnel.value.id,
        conversationId,
        actualStageKey
      );

      // Success - clear optimistic update tracking
      optimisticUpdates.delete(conversationId);

      // PERSISTENCE FIX: Update the conversation's funnel_mappings in Vuex store
      // This ensures the data persists on page refresh
      if (response.data?.mapping) {
        const conv = conversationList.value.find(c => c.id === conversationId);
        if (conv) {
          // Build updated funnel_mappings array
          const currentMappings = conv.funnel_mappings || [];
          const newMapping = response.data.mapping;
          const existingIndex = currentMappings.findIndex(
            m =>
              m.funnel_id === newMapping.funnel_id ||
              String(m.funnel_id) === String(newMapping.funnel_id)
          );

          let updatedMappings;
          if (existingIndex >= 0) {
            updatedMappings = [...currentMappings];
            updatedMappings[existingIndex] = newMapping;
          } else {
            updatedMappings = [...currentMappings, newMapping];
          }

          // Update the conversation in Vuex store with new funnel_mappings
          // NOTE: updated_at must be a float (Unix timestamp) to match API format
          store.commit('UPDATE_CONVERSATION', {
            id: conversationId,
            funnel_mappings: updatedMappings,
            updated_at: Date.now() / 1000, // Float Unix timestamp like API returns
          });
        }
      }
    } catch (error) {
      // Rollback on error
      const optimistic = optimisticUpdates.get(conversationId);
      if (optimistic) {
        // FIX: Create new object to ensure Vue reactivity triggers properly
        modifiedStages.value = {
          ...modifiedStages.value,
          [conversationId]: optimistic.previousStage,
        };
        saveStages({ [conversationId]: optimistic.previousStage });
        optimisticUpdates.delete(conversationId);
      }

      // Handle 404 - conversation deleted
      if (error?.response?.status === 404) {
        const index = conversations.value.findIndex(
          c => c.id === conversationId
        );
        if (index !== -1) {
          conversations.value.splice(index, 1);
        }
        // FIX: Create new object without the deleted key
        const { [conversationId]: removed, ...rest } = modifiedStages.value;
        modifiedStages.value = rest;
        useAlert(t('KANBAN.ERRORS.CONVERSATION_NOT_FOUND'));
        return;
      }

      useAlert(t('KANBAN.ERRORS.MOVE_FAILED'));
    }

    // Emit event immediately (don't wait for API)
    window.dispatchEvent(
      new CustomEvent('conversation-stage-changed', {
        detail: {
          conversationId,
          fromStage: actualFromKey,
          toStage: actualStageKey,
          funnelId: currentFunnel.value.id,
        },
      })
    );

    if (viewMode.value === 'crm') await fetchCrmSummary();

    // REMOVED: No more fetchConversations() after each move
    // The optimistic update is sufficient for a smooth UX
  } catch {
    // Error handled silently
  } finally {
    // Bug #4-5 Fix: Sempre liberar o lock
    release();
  }
};

const handleCardRemove = async conversation => {
  try {
    // Buscar o mapping da conversa com o funil atual
    const mapping = conversation.funnel_mappings?.find(
      m => String(m.funnel_id) === String(currentFunnel.value.id)
    );

    if (!mapping) {
      useAlert(t('KANBAN.ERRORS.REMOVE_NO_MAPPING'));
      return;
    }

    // Chamar API para remover
    await ConversationFunnelMappingsAPI.removeFromFunnel(
      conversation.id,
      mapping.id
    );

    // Remover conversa da lista local
    const index = conversations.value.findIndex(c => c.id === conversation.id);
    if (index !== -1) {
      conversations.value.splice(index, 1);
    }

    // Remover dos stages modificados
    // FIX: Create new object without the deleted key to ensure Vue reactivity
    const { [conversation.id]: removed, ...rest } = modifiedStages.value;
    modifiedStages.value = rest;

    // Bug #7 Fix: Remove localStorage direct writes - use Vuex only
    // Data is managed in backend and Vuex store, localStorage is only for caching via useKanbanStorage

    useAlert(
      t('KANBAN.REMOVED_FROM_FUNNEL', { funnelName: currentFunnel.value.name })
    );

    // Recarregar conversas
    await fetchConversations();
  } catch (error) {
    useAlert(t('KANBAN.ERRORS.REMOVE_FAILED'));
  }
};

// Transfer to funnel handlers
const handleTransferToFunnel = conversation => {
  transferConversation.value = conversation;
  selectedTargetFunnel.value = null;
  selectedTargetStage.value = null;
  const mapping = mappingForCurrentFunnel(conversation);
  removeFromSourceOnTransfer.value = mapping?.deal_status !== 'won';
  showTransferFunnelModal.value = true;
};

const transferStartsNewJourney = computed(
  () =>
    mappingForCurrentFunnel(transferConversation.value || {})?.deal_status ===
    'won'
);

// Contato do card: o serializer padrão de conversas expõe o contato em
// meta.sender (não em .contact) e o display_id no campo id — resolvemos ambos
// os formatos para não mostrar "#undefined".
const transferContactName = computed(() => {
  const c = transferConversation.value;
  if (!c) return '';
  return (
    c.meta?.sender?.name || c.contact?.name || `#${c.display_id || c.id || ''}`
  );
});
const transferContactSubtitle = computed(() => {
  const c = transferConversation.value;
  const sender = c?.meta?.sender || c?.contact;
  return sender?.phone_number || sender?.email || '';
});
const transferContactInitial = computed(
  () =>
    transferContactName.value
      .replace(/^#/, '')
      .trim()
      .charAt(0)
      .toUpperCase() || '?'
);

const availableTargetFunnels = computed(() => {
  if (!funnels.value) return [];
  return funnels.value.filter(
    f => f.id !== currentFunnel.value?.id && f.active !== false
  );
});

const targetFunnelStages = computed(() => {
  if (!selectedTargetFunnel.value?.stages) return [];
  return Object.entries(selectedTargetFunnel.value.stages)
    .sort((a, b) => (a[1].position || 0) - (b[1].position || 0))
    .map(([name, config]) => ({ name, ...config }));
});

const confirmTransferToFunnel = async () => {
  if (!selectedTargetFunnel.value || !selectedTargetStage.value) return;

  isTransferring.value = true;
  try {
    await KanbanAPI.transferToFunnel(
      currentFunnel.value.id,
      // O serializer padrão de conversas expõe o display_id no campo `id`
      // (não há `display_id` separado no objeto do board) — ver kanban_controller.
      transferConversation.value.display_id || transferConversation.value.id,
      selectedTargetFunnel.value.id,
      selectedTargetStage.value,
      removeFromSourceOnTransfer.value
    );

    useAlert(
      t('KANBAN.TRANSFER.SUCCESS', {
        funnelName: selectedTargetFunnel.value.name,
      })
    );

    showTransferFunnelModal.value = false;
    transferConversation.value = null;

    // Reload if removed from source
    if (removeFromSourceOnTransfer.value) {
      await fetchConversations();
    }
  } catch (error) {
    const msg = error?.response?.data?.error || t('KANBAN.TRANSFER.ERROR');
    useAlert(msg);
  } finally {
    isTransferring.value = false;
  }
};

const closeTransferModal = () => {
  showTransferFunnelModal.value = false;
  transferConversation.value = null;
};

const handleWinDealConfirm = async data => {
  isMarkingDealWon.value = true;
  try {
    // Chamar API para marcar como ganho
    await KanbanAPI.markDealAsWon(currentFunnel.value.id, data.conversationId, {
      final_value: data.finalValue,
      close_date: data.closeDate,
      notes: data.notes,
      won_reason: data.wonReason,
      won_reason_id: data.wonReasonId,
    });

    useAlert(t('KANBAN.DEAL.WIN.SUCCESS'));
    showWinDealModal.value = false;
    selectedDealConversation.value = null;
    selectedDealMapping.value = null;

    await applyPendingTerminalMove(data.conversationId);

    // Recarregar conversas para refletir a mudança
    await fetchConversations(true);
  } catch (error) {
    useAlert(dealActionErrorMessage(error, t('KANBAN.DEAL.WIN.ERROR')));
  } finally {
    isMarkingDealWon.value = false;
  }
};

const handleLoseDealConfirm = async data => {
  try {
    // Chamar API para marcar como perdido
    await KanbanAPI.markDealAsLost(
      currentFunnel.value.id,
      data.conversationId,
      {
        reason: data.reason,
        reason_id: data.reasonId,
        notes: data.notes,
      }
    );

    useAlert(t('KANBAN.DEAL.LOSE.SUCCESS'));
    showLoseDealModal.value = false;
    selectedDealConversation.value = null;
    selectedDealMapping.value = null;

    await applyPendingTerminalMove(data.conversationId);

    // Recarregar conversas para refletir a mudança
    await fetchConversations(true);
  } catch (error) {
    useAlert(dealActionErrorMessage(error, t('KANBAN.DEAL.LOSE.ERROR')));
  }
};

const closeWinDealModal = () => {
  showWinDealModal.value = false;
  selectedDealConversation.value = null;
  selectedDealMapping.value = null;
  // Cancelou o fechamento: o card NÃO vai para a coluna terminal.
  pendingTerminalMove.value = null;
};

const closeLoseDealModal = () => {
  showLoseDealModal.value = false;
  selectedDealConversation.value = null;
  selectedDealMapping.value = null;
  pendingTerminalMove.value = null;
};

// Deal Details Sidebar handlers
const handleViewDetails = ({ conversation, mapping }) => {
  selectedDealConversation.value = conversation;
  selectedDealMapping.value = mapping;
  showDealDetailsSidebar.value = true;
};

const closeDealDetailsSidebar = () => {
  showDealDetailsSidebar.value = false;
  selectedDealConversation.value = null;
  selectedDealMapping.value = null;
};

const handleDealUpdate = async () => {
  // Refresh conversations to get updated data
  await fetchConversations(true);
};

// Handle inline value update from KanbanCard
const handleUpdateTemperature = async ({ conversation, temperature }) => {
  try {
    await KanbanAPI.updateLeadTemperature(
      currentFunnel.value.id,
      conversation.display_id || conversation.id,
      temperature
    );
    // Optimistic update
    if (conversation) {
      conversation.lead_temperature = temperature;
    }
  } catch (error) {
    useAlert('Erro ao atualizar temperatura');
  }
};

const handleUpdateValue = async ({ conversation, value }) => {
  try {
    await KanbanAPI.updateDealValue(
      currentFunnel.value.id,
      conversation.id,
      value
    );
    useAlert(t('KANBAN.DEAL.VALUE_UPDATED'));

    // Refresh conversations to get updated data from server
    await fetchConversations(true);
  } catch (error) {
    useAlert(t('KANBAN.DEAL.VALUE_UPDATE_ERROR'));
  }
};

// Handle inline appointment update from KanbanCard
const handleUpdateAppointment = async ({
  conversation,
  date,
  appointmentId,
  meetingProvider,
}) => {
  try {
    if (!date) return;

    const payload = {
      // O datetime-local vem SEM fuso ("2026-07-21T14:30"); mandar cru fazia o
      // backend ler como UTC e a reunião "voltar" 3h. toISOString fixa o fuso.
      scheduled_at: new Date(date).toISOString(),
      status: 'scheduled',
      event_type: 'event',
      title: t('KANBAN.DEAL.APPOINTMENT_DEFAULT_TITLE'),
    };
    if (meetingProvider) payload.meeting_provider = meetingProvider;
    // Reagenda o existente (evita duplicata) ou cria um novo.
    if (appointmentId) {
      await ConversationAppointmentsAPI.update(
        conversation.id,
        appointmentId,
        payload
      );
    } else {
      await ConversationAppointmentsAPI.create(conversation.id, payload);
    }

    useAlert(t('KANBAN.DEAL.APPOINTMENT_UPDATED'));
    // Refresh to get updated appointment data
    await fetchConversations(true);
  } catch (error) {
    // Error handled by useAlert
    useAlert(t('KANBAN.DEAL.APPOINTMENT_UPDATE_ERROR'));
  }
};

// Handle quick message from KanbanCard
const handleSendQuickMessage = async ({
  conversation,
  message,
  onSuccess,
  onError,
}) => {
  try {
    // Send message via Vuex action
    await store.dispatch('createPendingMessageAndSend', {
      conversationId: conversation.id,
      message: message,
      private: false,
    });

    useAlert(t('KANBAN.QUICK_MESSAGE.SUCCESS'));
    if (onSuccess) onSuccess();
  } catch (error) {
    useAlert(t('KANBAN.QUICK_MESSAGE.ERROR'));
    if (onError) onError();
  }
};

const handleSidebarWinDeal = data => {
  showDealDetailsSidebar.value = false;
  handleWinDeal(data);
};

const handleSidebarLoseDeal = data => {
  showDealDetailsSidebar.value = false;
  handleLoseDeal(data);
};

const handleSidebarMoveToStage = async data => {
  const { conversation, mapping, newStage } = data;
  if (!conversation || !newStage) return;

  const currentStage = mapping?.stage_name || '';

  // Use the existing handleCardMove function
  await handleCardMove({
    conversationId: conversation.id,
    fromStage: currentStage,
    toStage: newStage,
  });

  // Refresh the sidebar data
  if (selectedDealMapping.value) {
    selectedDealMapping.value = {
      ...selectedDealMapping.value,
      stage_name: newStage,
    };
  }
};

// Event listeners
const handleFunnelChange = async event => {
  // Recarregar conversas para o novo funil
  if (event.detail.newFunnelId) {
    try {
      await store.dispatch('fetchAllConversations');
    } catch (error) {
      // Fallback para busca geral se der erro
      await store.dispatch('fetchAllConversations');
    }
  }
};

// Ref para armazenar o intervalo
const refreshInterval = ref(null);

// Auto-recarregar conversas periodicamente
const startAutoRefresh = () => {
  // PERFORMANCE: 120 seconds - WebSocket handles real-time updates
  refreshInterval.value = setInterval(() => {
    if (currentFunnel.value && !isFetching.value) {
      fetchConversations();
    }
  }, 120000);
};

// Listener para conversas adicionadas ao funil (definido fora para ser acessível no cleanup)
const handleConversationAddedToFunnel = event => {
  const { conversationId, funnelId, stageName, mapping } = event.detail;

  // Se é do funil atual, atualizar conversas
  if (String(funnelId) === String(currentFunnel.value?.id)) {
    // Buscar se a conversa já existe localmente
    const existingConv = conversations.value.find(c => c.id === conversationId);

    if (existingConv) {
      // Atualizar conversa existente
      // NÃO atualizar stage_name globalmente
      if (existingConv.funnel_mappings) {
        existingConv.funnel_mappings.push(mapping);
      } else {
        existingConv.funnel_mappings = [mapping];
      }

      // Atualizar stage modificado
      // FIX: Create new object to ensure Vue reactivity triggers
      modifiedStages.value = {
        ...modifiedStages.value,
        [conversationId]: stageName,
      };
    } else {
      // Conversa não existe, recarregar todas
      fetchConversations();
    }
  }
};

// Listener para conversas removidas do funil (definido fora para ser acessível no cleanup)
const handleConversationRemovedFromFunnel = event => {
  const { funnelId } = event.detail;

  // Se é do funil atual, recarregar conversas
  if (String(funnelId) === String(currentFunnel.value?.id)) {
    fetchConversations();
  }
};

// Listener para atualizações de mapeamento de funil
const handleFunnelMappingUpdate = event => {
  const { conversation_id, funnel_id, stage_name, mapping } = event.detail;

  // Verificar se é do funil atual
  if (String(funnel_id) === String(currentFunnel.value?.id)) {
    const conv = conversations.value.find(c => c.id === conversation_id);
    if (conv) {
      // NÃO atualizar stage_name globalmente - apenas registrar para este funil

      // Atualizar funnel_mappings
      if (conv.funnel_mappings) {
        const existingMapping = conv.funnel_mappings.find(
          m => String(m.funnel_id) === String(funnel_id)
        );
        if (existingMapping) {
          Object.assign(existingMapping, mapping);
        } else {
          conv.funnel_mappings.push(mapping);
        }
      } else {
        conv.funnel_mappings = [mapping];
      }

      // Bug #4-5 & #7 Fix: Update stage optimistically
      // FIX: Create new object to ensure Vue reactivity triggers
      modifiedStages.value = {
        ...modifiedStages.value,
        [conversation_id]: stage_name,
      };
    } else {
      // Se não encontrou a conversa, recarregar
      fetchConversations();
    }
  }
};

// Listener para atualizações de stage da conversa
const handleConversationStageUpdate = event => {
  const { conversation, stage_name, funnel_id } = event.detail;

  // Verificar se é do funil atual
  if (funnel_id && String(funnel_id) === String(currentFunnel.value?.id)) {
    const conv = conversations.value.find(c => c.id === conversation.id);
    if (conv) {
      // Atualizar conversa local
      Object.assign(conv, conversation);
      conv.stage_name = stage_name;

      // Bug #4-5 & #7 Fix: Update stage optimistically
      // FIX: Create new object to ensure Vue reactivity triggers
      modifiedStages.value = {
        ...modifiedStages.value,
        [conversation.id]: stage_name,
      };
    }
  }
};

// Lifecycle
onMounted(async () => {
  isInitializing.value = true;
  viewMode.value = localStorage.getItem('kanban_view_mode') || 'conversations';
  await store.dispatch('agents/get');

  // PERSISTENCE FIX: Clear localStorage on page refresh to rely only on API data
  // The API's funnel_mappings is the source of truth
  if (currentAccountId.value) {
    kanbanStorage.removeItem('conversation_stages', currentAccountId.value);
  }

  // Adicionar listeners para eventos globais
  window.addEventListener('funnel-mapping-updated', handleFunnelMappingUpdate);
  window.addEventListener(
    'conversation-stage-updated',
    handleConversationStageUpdate
  );

  // Importar e configurar emitter para escutar eventos
  import('shared/helpers/mitt').then(({ emitter }) => {
    // Escutar mudanças de stage de outros usuários
    emitter.on('conversation:stage:changed', data => {
      // Se é do funil atual, atualizar imediatamente
      if (
        data.funnelId &&
        String(data.funnelId) === String(currentFunnel.value?.id)
      ) {
        const conv = conversations.value.find(
          c => c.id === data.conversationId
        );
        if (conv) {
          conv.stage_name = data.stageName;
          if (data.conversation) {
            Object.assign(conv, data.conversation);
          }
        }
        // Bug #4-5 & #7 Fix: Update stage optimistically
        // FIX: Create new object to ensure Vue reactivity triggers
        modifiedStages.value = {
          ...modifiedStages.value,
          [data.conversationId]: data.stageName,
        };
      }
    });
  });

  // Configurar WebSocket para escutar atualizações de funil
  const accountId = store.getters.getCurrentAccountId;
  if (accountId && window.chatwootPubsubToken) {
    // Importar ActionCable com caminho correto
    import('shared/helpers/BaseActionCableConnector')
      .then(({ default: BaseConnector }) => {
        const websocketURL = window.chatwootConfig?.websocketURL || '';
        const pubsubToken = window.chatwootPubsubToken;

        // Criar conexão específica para funil updates
        const cable = new BaseConnector({}, pubsubToken, websocketURL);

        // Inscrever no canal de atualizações de funil
        const channelName = `account_${accountId}_funnel_updates`;

        cable.subscribe(channelName, {
          received: data => {
            if (viewMode.value === 'crm') fetchCrmSummary();
            if (data.event === 'stage_changed' && data.data) {
              const mappingData = data.data;

              // Verificar se é do funil atual
              if (
                String(mappingData.funnel_id) ===
                String(currentFunnel.value?.id)
              ) {
                // Buscar a conversa local
                const conv = conversations.value.find(
                  c =>
                    c.id === mappingData.conversation_id ||
                    c.display_id === mappingData.conversation_id
                );

                if (conv) {
                  // Atualizar o stage da conversa
                  conv.stage_name = mappingData.stage_name;

                  // Atualizar funnel_mappings se existir
                  if (conv.funnel_mappings) {
                    const mapping = conv.funnel_mappings.find(
                      m => String(m.funnel_id) === String(mappingData.funnel_id)
                    );
                    if (mapping) {
                      mapping.stage_name = mappingData.stage_name;
                      mapping.stage_label = mappingData.stage_label;
                      mapping.stage_color = mappingData.stage_color;
                      mapping.stage_entered_at = mappingData.stage_entered_at;
                    }
                  }

                  // Forçar re-renderização
                  nextTick(() => {});
                } else {
                  // Se não encontrou a conversa, pode ser que ela foi adicionada ao funil
                  // Recarregar conversas
                  fetchConversations();
                }
              }
            } else if (data.event === 'mapping_created' && data.data) {
              // Nova conversa adicionada ao funil
              if (
                String(data.data.funnel_id) === String(currentFunnel.value?.id)
              ) {
                fetchConversations();
              }
            } else if (data.event === 'stage_moved' && data.data) {
              // Conversa movida para outro estágio no funil
              // PERFORMANCE: Não refetch tudo - apenas atualizar o stage localmente
              if (
                String(data.data.funnel_id) === String(currentFunnel.value?.id)
              ) {
                const convId = data.data.conversation_id;
                const newStage = data.data.stage_name;
                // Atualizar apenas o stage modificado localmente
                if (convId && newStage) {
                  // FIX: Create new object to ensure Vue reactivity triggers
                  modifiedStages.value = {
                    ...modifiedStages.value,
                    [convId]: newStage,
                  };
                }
              }
            } else if (data.event === 'mapping_removed' && data.data) {
              // Conversa removida do funil
              if (
                String(data.data.funnel_id) === String(currentFunnel.value?.id)
              ) {
                fetchConversations();
              }
            }
          },
        });

        // Salvar referência para limpar no unmount
        window.kanbanFunnelCable = cable;
      })
      .catch(() => {
        // Fallback: usar refresh periódico se WebSocket falhar
        startAutoRefresh();
      });
  }

  // Adicionar listeners (funções já definidas fora do onMounted)
  window.addEventListener(
    'conversation-added-to-funnel',
    handleConversationAddedToFunnel
  );
  window.addEventListener(
    'conversation-removed-from-funnel',
    handleConversationRemovedFromFunnel
  );

  // Iniciar auto-refresh
  startAutoRefresh();

  try {
    // Carregar dados necessários
    await store.dispatch('inboxes/get');

    // F5 fix + perf: pre-set selectedFunnelId ANTES de qualquer fetch para
    // que conversationFilters.funnelId seja propagado ao backend já no
    // primeiro request (filtra /conversations por funil ativo).
    const funnelIdFromRoute = props.funnelId || route.query.funnel_id;
    if (funnelIdFromRoute) {
      selectedFunnelId.value = funnelIdFromRoute;
    }

    // Carregar funis ANTES do primeiro fetch para que o funnel_id já esteja
    // definido (se não veio da URL, loadFunnels seleciona o primeiro disponível).
    await loadFunnels();

    // Garantir que o funil correto está carregado (se vier da URL e o
    // loadFunnels não tiver disparado o select corretamente).
    if (
      funnelIdFromRoute &&
      String(currentFunnel.value?.id) !== String(funnelIdFromRoute)
    ) {
      await selectFunnel(funnelIdFromRoute);
    }

    // Bug #20 & #21 Fix: Force refresh on initial load to ensure ALL conversations are loaded
    // This prevents inconsistent loading by clearing cache and fetching complete data
    await fetchConversations(true);

    // Verificar se deve abrir uma conversa específica (vindo do botão de acesso rápido)
    const openConversationId = localStorage.getItem('kanban_open_conversation');
    if (openConversationId) {
      localStorage.removeItem('kanban_open_conversation');

      // Aguardar um pouco para garantir que o Kanban carregou
      setTimeout(() => {
        const conversation = conversations.value.find(
          c => String(c.id) === String(openConversationId)
        );

        if (conversation) {
          openConversationModal(conversation);
        }
      }, 1500);
    }

    // PERSISTENCE FIX: Don't load from localStorage on page refresh
    // The API data (funnel_mappings) is the source of truth
    // localStorage is only used for optimistic updates during drag-and-drop
    // Clearing modifiedStages ensures we use the server data
    modifiedStages.value = {};

    // Se não houver funis, criar funis padrão localmente
    if (funnels.value.length === 0) {
      try {
        await store.dispatch('funnels/createFromTemplate', {
          templateType: 'sales',
          customizations: {
            name: 'Funil de Vendas',
            is_default: true,
          },
        });
        await loadFunnels();
      } catch (error) {
        // loadFunnels já cria funis locais se não houver no backend
      }
    }

    // Garantir que temos pelo menos um funil selecionado
    if (funnels.value.length > 0) {
      // Se já há um funil selecionado no composable, garantir que o currentFunnel está sincronizado
      if (selectedFunnelId.value && currentFunnel.value) {
        isInitializing.value = false;
      } else if (selectedFunnelId.value && !currentFunnel.value) {
        // Funil selecionado mas não carregado, forçar carregamento
        await selectFunnel(selectedFunnelId.value);
        isInitializing.value = false;
      } else {
        // Nenhum funil selecionado, selecionar o padrão
        let defaultFunnel = funnels.value.find(
          f => f.settings?.is_default === true
        );
        if (!defaultFunnel) {
          defaultFunnel = funnels.value.find(f => f.funnel_type === 'sales');
        }
        if (!defaultFunnel) {
          defaultFunnel = funnels.value[0];
        }

        if (defaultFunnel) {
          // Selecionar o funil usando o método do composable
          await selectFunnel(defaultFunnel.id);
          isInitializing.value = false;
        }
      }
    } else {
      isInitializing.value = false;
    }

    // Adicionar listener para mudanças de funil
    window.addEventListener('funnel-changed', handleFunnelChange);

    // Adicionar listener REAL-TIME para mudanças de stage
    window.addEventListener('conversation-stage-updated', event => {
      const { conversation, stage_name, funnel_id } = event.detail;

      // Se é do funil atual ou sem funil especificado
      if (!funnel_id || String(funnel_id) === String(currentFunnel.value?.id)) {
        // Bug #4-5 & #7 Fix: Update optimistically
        // FIX: Create new object to ensure Vue reactivity triggers
        modifiedStages.value = {
          ...modifiedStages.value,
          [conversation.id]: stage_name,
        };

        // Forçar re-render IMEDIATO
        nextTick(() => {});
      }
    });
  } catch (error) {
    isInitializing.value = false;
  }
});

// Bug #14, #15, #18 Fix: Cleanup completo de todos os recursos
onBeforeUnmount(() => {
  crmRequestId += 1;
  clearTimeout(crmFilterTimer);
  // Bug #15 Fix: Remover TODOS os event listeners
  window.removeEventListener('funnel-changed', handleFunnelChange);
  window.removeEventListener(
    'conversation-stage-updated',
    handleConversationStageUpdate
  );
  window.removeEventListener(
    'funnel-mapping-updated',
    handleFunnelMappingUpdate
  );
  window.removeEventListener(
    'conversation-added-to-funnel',
    handleConversationAddedToFunnel
  );
  window.removeEventListener(
    'conversation-removed-from-funnel',
    handleConversationRemovedFromFunnel
  );

  // Bug #18 Fix: Limpar conexão WebSocket completamente
  if (window.kanbanFunnelCable) {
    try {
      // Unsubscribe de todos os canais
      const accountId = store.getters.getCurrentAccountId;
      if (accountId) {
        window.kanbanFunnelCable.unsubscribe(
          `account_${accountId}_funnel_updates`
        );
        window.kanbanFunnelCable.unsubscribe(
          `account_${accountId}_conversations`
        );
      }
      // Desconectar completamente
      window.kanbanFunnelCable.disconnect();
    } catch {
      // Error handled silently
    } finally {
      window.kanbanFunnelCable = null;
    }
  }

  // Bug #14 Fix: Limpar interval de auto-refresh
  if (refreshInterval.value) {
    clearInterval(refreshInterval.value);
    refreshInterval.value = null;
  }

  // Bug #14 Fix: Limpar debounce timers
  if (fetchDebounceTimer) {
    clearTimeout(fetchDebounceTimer);
    fetchDebounceTimer = null;
  }

  // Bug #14 Fix: Limpar qualquer fetch timeout pendente
  if (fetchTimeout.value) {
    clearTimeout(fetchTimeout.value);
    fetchTimeout.value = null;
  }
});

// Watch para recarregar conversas quando trocar de funil
watch(
  currentFunnel,
  async (newFunnel, oldFunnel) => {
    if (newFunnel && newFunnel.id !== oldFunnel?.id) {
      // Bug #14 Fix: Clear interval when switching funnels to prevent memory leak
      if (refreshInterval.value) {
        clearInterval(refreshInterval.value);
        refreshInterval.value = null;
      }

      // PERSISTENCE FIX: Clear modifiedStages when switching funnels
      // API data (funnel_mappings) is the source of truth, not localStorage
      modifiedStages.value = {};

      // Resetar filtros avançados ao trocar de funil
      appliedFilter.value = [];
      store.dispatch('clearConversationFilters');

      // Buscar conversas com filtros do novo funil
      fetchConversations();

      // Bug #14 Fix: Restart auto-refresh for the new funnel
      startAutoRefresh();
    }
  },
  { immediate: false, deep: true }
);

// Watch para recarregar quando mudar filtros básicos - COM PROTEÇÃO
watch([activeStatus, activeSortBy, activeAssigneeTab, viewMode], () => {
  // Cancelar timer anterior se existir
  if (fetchDebounceTimer) {
    clearTimeout(fetchDebounceTimer);
  }

  // Aguardar 300ms antes de buscar conversas (debounce)
  fetchDebounceTimer = setTimeout(() => {
    // Reset tentativas ao mudar filtros manualmente
    fetchAttempts.value = 0;

    nextTick(() => {
      if (!isFetching.value) {
        fetchConversations();
      }
    });
  }, 300);
});

watch(
  [viewMode, currentAccountId, selectedFunnelId, crmQuery],
  () => {
    clearTimeout(crmFilterTimer);
    crmRequestId += 1;
    crmConversations.value = [];
    crmSummary.value = {};
    isLoadingCrmSummary.value = viewMode.value === 'crm';
    crmFilterTimer = setTimeout(fetchCrmSummary, 200);
  },
  { deep: true }
);
</script>

<template>
  <div class="kanban-container flex flex-col h-full w-full overflow-hidden">
    <!-- Header Reconstruído -->
    <div class="sticky top-0 z-50 bg-n-solid-1 border-b border-n-weak">
      <div class="flex items-center justify-between px-6 h-14">
        <!-- Esquerda -->
        <div class="flex items-center gap-3 min-w-0 flex-1">
          <div class="flex items-center rounded-lg bg-n-alpha-1 p-0.5">
            <button
              v-for="mode in ['conversations', 'crm']"
              :key="mode"
              type="button"
              class="rounded-md px-3 py-1.5 text-xs font-semibold transition-colors"
              :class="
                viewMode === mode
                  ? 'bg-n-solid-1 text-n-slate-12 shadow-sm'
                  : 'text-n-slate-10 hover:text-n-slate-12'
              "
              @click="setViewMode(mode)"
            >
              {{
                mode === 'crm'
                  ? t('KANBAN_VIEW.CRM.TITLE')
                  : t('KANBAN_VIEW.CONVERSATIONS')
              }}
            </button>
          </div>

          <div
            v-if="viewMode === 'conversations' && activeChannels.length > 0"
            class="flex items-center gap-1"
          >
            <div
              v-for="channel in activeChannels"
              :key="channel.type"
              class="flex items-center gap-1 px-1.5 py-0.5 bg-n-alpha-1 rounded"
              :title="`${channel.inboxes.join(', ')} (${channel.count} conversas)`"
            >
              <i class="text-sm" :class="[channel.icon, channel.color]" />
              <span class="text-xs font-medium text-n-slate-11">
                {{ channel.count }}
              </span>
            </div>
          </div>

          <div class="h-4 w-px bg-n-weak" />

          <div class="flex items-center gap-1">
            <div v-if="viewMode === 'conversations'" class="relative">
              <NextButton
                id="toggleConversationFilterButton"
                v-tooltip.right="$t('FILTER.TOOLTIP_LABEL')"
                icon="i-lucide-list-filter"
                slate
                xs
                faded
                @click="handleFiltersModal"
              />
              <div
                id="conversationFilterTeleportTarget"
                class="absolute z-40 mt-2"
              />
            </div>
            <ConversationBasicFilter
              v-if="viewMode === 'conversations'"
              :is-on-expanded-layout="false"
              @change-filter="onBasicFilterChange"
            />

            <!-- Busca de leads: só a lupa, junto dos filtros; clique abre popover
                 com o input. Lupa fica azul enquanto há busca ativa. -->
            <div v-on-clickaway="closeSearchIfEmpty" class="relative">
              <NextButton
                v-tooltip.bottom="t('KANBAN_VIEW.SEARCH_PLACEHOLDER')"
                icon="i-lucide-search"
                xs
                faded
                :slate="!searchQuery"
                :color="searchQuery ? 'blue' : undefined"
                @click="searchOpen ? closeSearchIfEmpty() : openSearch()"
              />
              <div
                v-if="searchOpen"
                class="absolute left-0 top-full z-50 mt-2 rounded-lg border border-n-weak bg-n-solid-2 p-1.5 shadow-lg"
              >
                <div class="relative flex items-center">
                  <i
                    class="i-lucide-search absolute left-2 text-sm text-n-slate-10 pointer-events-none"
                  />
                  <input
                    ref="searchInputRef"
                    v-model="searchQuery"
                    type="search"
                    :placeholder="t('KANBAN_VIEW.SEARCH_PLACEHOLDER')"
                    class="h-8 w-64 rounded-lg border border-n-weak bg-n-solid-1 pl-7 pr-6 text-xs text-n-slate-12 placeholder:text-n-slate-10 focus:border-n-brand focus:outline-none reset-base"
                    @keydown.esc="clearSearch"
                  />
                  <button
                    v-if="searchQuery"
                    type="button"
                    class="absolute right-1.5 text-n-slate-10 hover:text-n-slate-12"
                    @click="clearSearch"
                  >
                    <i class="i-lucide-x text-xs" />
                  </button>
                </div>
              </div>
            </div>

            <NextButton
              v-tooltip.bottom="t('KANBAN_VIEW.EXPORT.BUTTON')"
              icon="i-lucide-download"
              xs
              faded
              slate
              :disabled="!currentFunnel"
              @click="openExportDialog"
            />
          </div>

          <div v-if="viewMode === 'conversations'" class="h-4 w-px bg-n-weak" />

          <ChatTypeTabs
            v-if="viewMode === 'conversations'"
            :items="assigneeTabItems"
            :active-tab="activeAssigneeTab"
            is-compact
            @chat-tab-change="updateAssigneeTab"
          />
        </div>

        <!-- Direita -->
        <div class="flex items-center gap-3 flex-shrink-0">
          <!-- Botão Novo Lead -->
          <NextButton
            icon="i-lucide-plus"
            color="blue"
            sm
            @click="openNewLeadModal"
          >
            {{ t('NEW_LEAD.TITLE') }}
          </NextButton>

          <div v-if="viewMode === 'conversations'" class="w-px bg-n-weak h-5" />

          <div
            v-if="viewMode === 'conversations'"
            class="flex items-center gap-1.5 px-2.5 bg-green-50 dark:bg-green-950/50 border border-green-200 dark:border-green-800 rounded-md h-8"
          >
            <i
              class="ri-money-dollar-circle-line text-sm text-green-600 dark:text-green-400 flex-shrink-0"
            />
            <span
              class="text-xs font-medium text-green-700 dark:text-green-300 whitespace-nowrap leading-8"
            >
              {{ formattedTotalGeneralValue }}
            </span>
          </div>

          <div v-if="viewMode === 'conversations'" class="w-px bg-n-weak h-5" />

          <!-- View Switcher (Kanban/Calendar) -->
          <div class="flex items-center gap-1 bg-n-alpha-1 p-0.5 rounded-lg">
            <button
              type="button"
              class="flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-colors bg-n-solid-1 text-n-slate-12 shadow-sm"
              :title="t('KANBAN_VIEW.KANBAN_VIEW')"
            >
              <i class="ri-layout-column-line text-sm" />
              <!-- eslint-disable-next-line vue/no-bare-strings-in-template -->
              <span class="hidden sm:inline">Pipeline</span>
            </button>
            <router-link
              :to="{ name: 'pipeline_calendar' }"
              class="flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-colors text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-2"
              :title="t('KANBAN_VIEW.CALENDAR_VIEW')"
            >
              <i class="ri-calendar-line text-sm" />
              <!-- eslint-disable-next-line vue/no-bare-strings-in-template -->
              <span class="hidden sm:inline">Calendário</span>
            </router-link>
          </div>

          <div class="w-px bg-n-weak h-5" />

          <!-- Funnel Dropdown Customizado -->
          <div
            v-on-clickaway="() => (showFunnelDropdown = false)"
            class="relative"
          >
            <button
              type="button"
              class="flex items-center gap-2 px-3 py-1.5 h-8 rounded-lg border border-n-weak bg-n-solid-1 hover:bg-n-alpha-2 transition-colors cursor-pointer"
              data-tour="pipeline-funil"
              @click="showFunnelDropdown = !showFunnelDropdown"
            >
              <fluent-icon
                icon="filter"
                size="14"
                class="text-n-slate-11 flex-shrink-0"
              />
              <span
                class="text-sm font-medium text-n-slate-12 max-w-40 truncate"
              >
                {{ currentFunnel?.name || t('KANBAN.SELECT_FUNNEL') }}
              </span>
              <fluent-icon
                icon="chevron-down"
                size="12"
                class="text-n-slate-11 flex-shrink-0 transition-transform"
                :class="{ 'rotate-180': showFunnelDropdown }"
              />
            </button>

            <!-- Dropdown Menu -->
            <div
              v-if="showFunnelDropdown"
              class="absolute top-full right-0 mt-1 min-w-48 max-w-64 bg-n-alpha-3 backdrop-blur-[100px] border border-n-weak dark:border-n-strong/50 rounded-lg shadow-lg z-50 p-1"
            >
              <button
                v-for="funnel in funnels"
                :key="funnel.id"
                type="button"
                class="flex items-center justify-between w-full px-3 py-2 text-sm rounded-md hover:bg-n-alpha-2 transition-colors"
                :class="{
                  'bg-n-alpha-2': selectedFunnelId === funnel.id,
                }"
                @click="
                  handleFunnelSelection(funnel.id);
                  showFunnelDropdown = false;
                "
              >
                <span class="text-n-slate-12 truncate">{{ funnel.name }}</span>
                <fluent-icon
                  v-if="selectedFunnelId === funnel.id"
                  icon="checkmark"
                  size="14"
                  class="text-n-blue-text flex-shrink-0 ml-2"
                />
              </button>

              <!-- Empty state -->
              <div
                v-if="funnels.length === 0"
                class="px-3 py-2 text-sm text-n-slate-11 text-center"
              >
                {{ t('KANBAN_VIEW.NO_FUNNEL_AVAILABLE') }}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="viewMode === 'crm'"
        class="flex min-h-12 flex-wrap items-center gap-2 border-t border-n-weak px-6 py-2"
      >
        <CrmPipelineFilters
          v-model="crmFilters"
          v-model:search="searchQuery"
          :agents="accountAgents || []"
          :stages="currentFunnel?.stages || {}"
          :storage-key="crmStorageKey"
          :timezone="crmTimezone"
        />
        <span v-if="crmLoadError" class="text-xs text-n-ruby-9">{{
          t('KANBAN_VIEW.CRM.SUMMARY_ERROR')
        }}</span>
        <div
          class="ml-auto flex items-center gap-4 text-xs text-n-slate-10"
          :class="{ 'animate-pulse': isLoadingCrmSummary }"
        >
          <span>
            <strong class="text-n-slate-12">{{ crmSummary.total || 0 }}</strong>
            {{ t('KANBAN_VIEW.CRM.TOTAL') }}
          </span>
          <span>
            <strong class="text-n-teal-11">{{ crmSummary.won || 0 }}</strong>
            {{ t('KANBAN_VIEW.CRM.WON') }}
          </span>
          <span>
            <strong class="text-n-slate-12">{{
              formatCrmValue(crmSummary.total_value)
            }}</strong>
          </span>
          <span>{{ crmStatusLabel }}</span>
        </div>
      </div>
    </div>

    <!-- Loading State ao trocar de funil ou inicializar -->
    <div
      v-if="isLoadingFunnel || isInitializing"
      class="flex-1 flex items-center justify-center"
    >
      <div class="flex flex-col items-center gap-3">
        <div
          class="h-10 w-10 animate-spin rounded-full border-[3px] border-n-alpha-2 border-t-n-brand"
        />
        <p class="text-sm font-medium text-n-slate-11">
          {{
            isInitializing
              ? t('KANBAN_VIEW.INITIALIZING')
              : t('KANBAN_VIEW.LOADING_FUNNEL')
          }}
        </p>
      </div>
    </div>

    <!-- Debug Info (disabled) -->
    <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
    <div v-if="false" class="p-4 bg-yellow-100 text-sm">
      <div>currentFunnel: {{ currentFunnel ? 'YES' : 'NO' }}</div>
      <div>isLoadingFunnel: {{ isLoadingFunnel }}</div>
      <div>isInitializing: {{ isInitializing }}</div>
      <div>conversationList.length: {{ conversationList.length }}</div>
    </div>
    <!-- eslint-enable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->

    <!-- Skeleton do board: 1º carregamento das conversas (payload pesado) -->
    <div
      v-if="
        currentFunnel &&
        !isLoadingFunnel &&
        !isInitializing &&
        showBoardSkeleton
      "
      class="flex-1 overflow-hidden p-4"
      data-testid="kanban-board-skeleton"
    >
      <div class="flex h-full gap-4">
        <div
          v-for="column in 4"
          :key="column"
          class="skeleton-col flex w-72 shrink-0 flex-col gap-3 rounded-xl border border-n-weak bg-n-solid-1 p-3"
          :style="{ animationDelay: `${(column - 1) * 90}ms` }"
        >
          <div class="flex items-center justify-between">
            <div class="skeleton-shimmer h-5 w-28 rounded-md" />
            <div class="skeleton-shimmer h-5 w-8 rounded-full" />
          </div>
          <div
            v-for="card in 3"
            :key="card"
            class="rounded-xl border border-n-weak bg-n-solid-2 p-3"
          >
            <div class="mb-2.5 flex items-center gap-2">
              <div class="skeleton-shimmer size-8 shrink-0 rounded-full" />
              <div class="min-w-0 flex-1">
                <div class="skeleton-shimmer mb-1.5 h-3.5 w-3/4 rounded" />
                <div class="skeleton-shimmer h-3 w-1/2 rounded" />
              </div>
            </div>
            <div class="skeleton-shimmer mb-2 h-3 w-2/3 rounded" />
            <div class="flex gap-1.5">
              <div class="skeleton-shimmer h-4 w-14 rounded-full" />
              <div class="skeleton-shimmer h-4 w-10 rounded-full" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Board Kanban de Conversas -->
    <KanbanBoard
      v-else-if="
        currentFunnel &&
        !isLoadingFunnel &&
        !isInitializing &&
        conversationList.length >= 0
      "
      :funnel="currentFunnel"
      :grouped-conversations="groupedConversations"
      class="flex-1 overflow-x-auto overflow-y-hidden"
      @card-click="handleCardClick"
      @card-move="handleCardMove"
      @card-remove="handleCardRemove"
      @transfer-to-funnel="handleTransferToFunnel"
      @win-deal="handleWinDeal"
      @lose-deal="handleLoseDeal"
      @view-details="handleViewDetails"
      @update-value="handleUpdateValue"
      @update-appointment="handleUpdateAppointment"
      @send-quick-message="handleSendQuickMessage"
      @update-temperature="handleUpdateTemperature"
      @add-lead="openNewLeadModal"
    />

    <!-- Empty State - Funil sem conversas -->
    <div
      v-if="
        currentFunnel &&
        !isLoadingFunnel &&
        !isInitializing &&
        conversationList.length === 0 &&
        false
      "
      class="flex-1 flex items-center justify-center"
    >
      <div class="text-center max-w-md">
        <fluent-icon icon="filter" size="60" class="text-slate-300 mb-4" />
        <h3
          class="text-lg font-semibold text-slate-700 dark:text-slate-300 mb-2"
        >
          {{ t('KANBAN_VIEW.NO_CONVERSATIONS') }}
        </h3>
        <p class="text-slate-500 dark:text-slate-400 mb-4">
          {{ t('KANBAN_VIEW.NO_CONVERSATIONS_DESC') }}
        </p>
        <ol
          class="text-left text-sm text-slate-600 dark:text-slate-400 space-y-2 mb-4"
        >
          <!-- eslint-disable @intlify/vue-i18n/no-raw-text, vue/no-bare-strings-in-template -->
          <li class="flex items-start">
            <span class="font-semibold mr-2">1.</span>
            <span>{{ t('KANBAN_VIEW.STEP_1') }}</span>
          </li>
          <li class="flex items-start">
            <span class="font-semibold mr-2">2.</span>
            <span>{{ t('KANBAN_VIEW.STEP_2') }}</span>
          </li>
          <li class="flex items-start">
            <span class="font-semibold mr-2">3.</span>
            <span>{{ t('KANBAN_VIEW.STEP_3') }}</span>
          </li>
          <!-- eslint-enable @intlify/vue-i18n/no-raw-text, vue/no-bare-strings-in-template -->
        </ol>
        <router-link
          to="/app/accounts/1/dashboard/home"
          class="inline-flex items-center px-4 py-2 bg-woot-500 text-white rounded-lg hover:bg-woot-600"
        >
          <fluent-icon icon="chat" size="16" class="mr-2" />
          {{ t('KANBAN_VIEW.GO_TO_CONVERSATIONS') }}
        </router-link>
      </div>
    </div>

    <!-- Empty State -->
    <div
      v-else-if="!currentFunnel && !isLoadingFunnel && !isInitializing"
      class="flex-1 flex items-center justify-center"
    >
      <div class="text-center">
        <fluent-icon icon="board" size="60" class="text-slate-300 mb-4" />
        <h3
          class="text-lg font-semibold text-slate-700 dark:text-slate-300 mb-2"
        >
          {{ t('KANBAN_VIEW.NO_FUNNEL_SELECTED') }}
        </h3>
        <p class="text-slate-500 dark:text-slate-400 mb-4">
          {{
            funnels.length > 0
              ? t('KANBAN_VIEW.SELECT_FUNNEL')
              : t('KANBAN_VIEW.CONFIGURING_FUNNELS')
          }}
        </p>
        <div v-if="funnels.length === 0" class="mt-4">
          <div
            class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"
          />
        </div>
      </div>
    </div>

    <!-- Modal de Filtros Avançados com TeleportWithDirection -->
    <TeleportWithDirection
      v-if="showAdvancedFilters"
      to="#conversationFilterTeleportTarget"
    >
      <ConversationFilter
        v-model="appliedFilter"
        folder-name=""
        :is-folder-view="false"
        @apply-filter="onApplyFilter"
        @close="closeAdvanceFiltersModal"
      />
    </TeleportWithDirection>

    <!-- Modal de Conversa -->
    <ConversationModal
      v-if="showConversationModal"
      :conversation-id="selectedConversationId"
      @close="showConversationModal = false"
    />

    <!-- Modal Novo Lead -->
    <NewLeadModal
      ref="newLeadModalRef"
      :current-funnel="currentFunnel"
      :funnels="funnels"
      @created="handleLeadCreated"
    />

    <Dialog
      ref="exportDialogRef"
      :title="t('KANBAN_VIEW.EXPORT.TITLE')"
      :description="t('KANBAN_VIEW.EXPORT.DESCRIPTION')"
      :confirm-button-label="t('KANBAN_VIEW.EXPORT.CONFIRM')"
      :is-loading="isExportingLeads"
      :disable-confirm-button="exportStageNames.length === 0"
      width="md"
      @confirm="exportLeads"
    >
      <div class="flex flex-col gap-5">
        <p v-if="viewMode === 'crm'" class="text-xs text-n-slate-10">
          {{ t('KANBAN_VIEW.CRM.FILTERS.EXPORT_HELP') }}
        </p>
        <label class="flex flex-col gap-1.5 text-sm text-n-slate-12">
          <span class="font-medium">{{ t('KANBAN_VIEW.EXPORT.STATUS') }}</span>
          <select
            v-model="exportDealStatus"
            :disabled="viewMode === 'crm'"
            class="h-9 rounded-lg border border-n-weak bg-n-solid-1 px-3 text-sm outline-none focus:border-n-brand"
          >
            <option value="all">
              {{ t('KANBAN_VIEW.EXPORT.STATUS_ALL') }}
            </option>
            <option value="open">
              {{ t('KANBAN_VIEW.EXPORT.STATUS_OPEN') }}
            </option>
            <option value="won">
              {{ t('KANBAN_VIEW.EXPORT.STATUS_WON') }}
            </option>
            <option value="lost">
              {{ t('KANBAN_VIEW.EXPORT.STATUS_LOST') }}
            </option>
          </select>
        </label>

        <fieldset class="flex flex-col gap-2">
          <legend class="mb-1 text-sm font-medium text-n-slate-12">
            {{ t('KANBAN_VIEW.EXPORT.STAGES') }}
          </legend>
          <label
            v-for="stage in exportStageOptions"
            :key="stage.name"
            class="flex items-center gap-2 text-sm text-n-slate-11"
          >
            <input
              v-model="exportStageNames"
              :disabled="viewMode === 'crm'"
              type="checkbox"
              :value="stage.name"
              class="size-4 rounded border-n-weak text-n-brand"
            />
            <span>{{ stage.label }}</span>
          </label>
        </fieldset>

        <label class="flex items-center gap-2 text-sm text-n-slate-11">
          <input
            v-model="includeCustomAttributes"
            type="checkbox"
            class="size-4 rounded border-n-weak text-n-brand"
          />
          <span>{{ t('KANBAN_VIEW.EXPORT.CUSTOM_ATTRIBUTES') }}</span>
        </label>
      </div>
    </Dialog>

    <!-- Modal Ganhar Deal -->
    <WinDealModal
      :show="showWinDealModal"
      :conversation="selectedDealConversation"
      :current-value="selectedDealValue"
      :is-submitting="isMarkingDealWon"
      @close="closeWinDealModal"
      @confirm="handleWinDealConfirm"
    />

    <!-- Modal Perder Deal -->
    <LoseDealModal
      :show="showLoseDealModal"
      :conversation="selectedDealConversation"
      @close="closeLoseDealModal"
      @confirm="handleLoseDealConfirm"
    />

    <!-- Modal Transferir para Outro Funil -->
    <div
      v-if="showTransferFunnelModal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      @click.self="closeTransferModal"
    >
      <div
        class="w-full max-w-md overflow-hidden rounded-2xl border border-n-weak bg-n-solid-2 shadow-2xl"
      >
        <!-- Header -->
        <div class="flex items-center justify-between p-5">
          <div class="flex items-center gap-2.5">
            <span
              class="flex h-8 w-8 items-center justify-center rounded-lg bg-n-brand/10 text-n-brand"
            >
              <span class="i-lucide-arrow-left-right text-base" />
            </span>
            <h3 class="text-base font-semibold text-n-slate-12">
              {{ $t('KANBAN.TRANSFER.TITLE') }}
            </h3>
          </div>
          <button
            type="button"
            class="flex h-7 w-7 items-center justify-center rounded-md text-n-slate-10 transition-colors hover:bg-n-alpha-2 hover:text-n-slate-12"
            @click="closeTransferModal"
          >
            <span class="i-lucide-x text-base" />
          </button>
        </div>

        <!-- Body -->
        <div class="space-y-4 px-5 pb-2">
          <!-- Contact info -->
          <div
            v-if="transferConversation"
            class="flex items-center gap-3 rounded-xl border border-n-weak bg-n-alpha-1 p-3"
          >
            <span
              class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-n-brand/10 text-sm font-semibold text-n-brand"
            >
              {{ transferContactInitial }}
            </span>
            <div class="min-w-0 flex-1">
              <p class="truncate text-sm font-semibold text-n-slate-12">
                {{ transferContactName }}
              </p>
              <p
                v-if="transferContactSubtitle"
                class="truncate text-xs text-n-slate-10"
              >
                {{ transferContactSubtitle }}
              </p>
            </div>
          </div>

          <!-- Select target funnel -->
          <div>
            <label class="mb-1.5 block text-sm font-medium text-n-slate-11">
              {{ $t('KANBAN.TRANSFER.SELECT_FUNNEL') }}
            </label>
            <select
              v-model="selectedTargetFunnel"
              class="w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 focus:border-n-brand focus:outline-none focus:ring-2 focus:ring-n-brand/40"
            >
              <option :value="null" disabled>
                {{ $t('KANBAN.TRANSFER.CHOOSE_FUNNEL') }}
              </option>
              <option
                v-for="funnel in availableTargetFunnels"
                :key="funnel.id"
                :value="funnel"
              >
                {{ funnel.name }}
              </option>
            </select>
          </div>

          <!-- Select target stage -->
          <div v-if="selectedTargetFunnel">
            <label class="mb-1.5 block text-sm font-medium text-n-slate-11">
              {{ $t('KANBAN.TRANSFER.SELECT_STAGE') }}
            </label>
            <select
              v-model="selectedTargetStage"
              class="w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 focus:border-n-brand focus:outline-none focus:ring-2 focus:ring-n-brand/40"
            >
              <option :value="null" disabled>
                {{ $t('KANBAN.TRANSFER.CHOOSE_STAGE') }}
              </option>
              <option
                v-for="stage in targetFunnelStages"
                :key="stage.name"
                :value="stage.name"
              >
                {{ stage.label || stage.name }}
              </option>
            </select>
          </div>

          <!-- Remove from source toggle -->
          <div
            v-if="transferStartsNewJourney"
            class="rounded-lg border border-n-weak bg-n-alpha-1 p-3 text-sm text-n-slate-11"
          >
            {{ $t('KANBAN.TRANSFER.NEW_JOURNEY_HINT') }}
          </div>
          <label
            v-else
            class="flex cursor-pointer items-center gap-3 rounded-lg py-1"
          >
            <span class="relative inline-flex items-center">
              <input
                v-model="removeFromSourceOnTransfer"
                type="checkbox"
                class="peer sr-only"
              />
              <span
                class="h-5 w-9 rounded-full bg-n-slate-4 transition-colors after:absolute after:left-[2px] after:top-[2px] after:h-4 after:w-4 after:rounded-full after:bg-white after:transition-all after:content-[''] peer-checked:bg-n-brand peer-checked:after:translate-x-full peer-focus:ring-2 peer-focus:ring-n-brand/40"
              />
            </span>
            <span class="text-sm text-n-slate-11">
              {{ $t('KANBAN.TRANSFER.REMOVE_FROM_SOURCE') }}
            </span>
          </label>
        </div>

        <!-- Footer -->
        <div class="mt-2 flex justify-end gap-2 p-5">
          <button
            type="button"
            class="rounded-lg px-4 py-2 text-sm font-medium text-n-slate-11 transition-colors hover:bg-n-alpha-2"
            @click="closeTransferModal"
          >
            {{ $t('KANBAN.TRANSFER.CANCEL') }}
          </button>
          <button
            type="button"
            :disabled="
              !selectedTargetFunnel || !selectedTargetStage || isTransferring
            "
            class="flex items-center gap-2 rounded-lg bg-n-brand px-4 py-2 text-sm font-semibold text-white transition-colors hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
            @click="confirmTransferToFunnel"
          >
            <span
              v-if="isTransferring"
              class="i-lucide-loader-circle animate-spin"
            />
            <span v-else class="i-lucide-arrow-left-right" />
            {{ $t('KANBAN.TRANSFER.CONFIRM') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Sidebar de Detalhes do Deal -->
    <DealDetailsSidebar
      :show="showDealDetailsSidebar"
      :conversation="selectedDealConversation"
      :mapping="selectedDealMapping"
      :funnel="currentFunnel"
      :stages="stagesArray"
      @close="closeDealDetailsSidebar"
      @win-deal="handleSidebarWinDeal"
      @lose-deal="handleSidebarLoseDeal"
      @move-to-stage="handleSidebarMoveToStage"
      @update="handleDealUpdate"
    />
  </div>
</template>

<style scoped>
/* Skeleton do board: shimmer (onda de luz) + colunas entrando em cascata.
   Keyframes não existem no Tailwind — bloco scoped só para o loading. */
.skeleton-col {
  animation: skeleton-enter 0.4s ease both;
}

@keyframes skeleton-enter {
  from {
    opacity: 0;
    transform: translateY(10px);
  }

  to {
    opacity: 1;
    transform: none;
  }
}

.skeleton-shimmer {
  position: relative;
  overflow: hidden;
  background: rgba(148, 163, 184, 0.14);
}

.skeleton-shimmer::after {
  content: '';
  position: absolute;
  inset: 0;
  transform: translateX(-100%);
  background: linear-gradient(
    90deg,
    transparent,
    rgba(148, 163, 184, 0.22),
    transparent
  );
  animation: skeleton-wave 1.4s infinite;
}

@keyframes skeleton-wave {
  100% {
    transform: translateX(100%);
  }
}
</style>
